import {
  createGridWellIngredient,
  createWellInPlate,
  createGridWellPlate,
  getConcentrationRange,
  getFirstWellNumber,
  getGridIngredients,
  getGrids,
  getLastWellNumber,
  getNumColumns,
  getNumRows,
  getNumWells,
  getPHRange,
  isHighPHIngredientStockIdNull,
  isPHNull,
} from 'business/optimization/GridOptimization';
import {
  GridWellBoundary,
  GRID_LAYER_BASE_NAME,
  OPTIMIZATION_DROP_NUMBER,
  PROTEIN_LAYER_BASE_NAME,
} from 'business/optimization/constants';
import {
  GridOptimizationConfig,
  GridOptimizationResult,
  GridWellIngredient,
  GridWells,
  LayerRect,
  ScoreBoundaryIds,
} from 'business/optimization/types';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  GridDTO,
  GridIngredientDTO,
  GridLayerDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
  ProteinLayerDTO,
  ProteinLayerProteinFormulationDTO,
  ScoresOfWellDropsDTO,
  ScoreDefinitionDTO,
} from 'business/models/Models';
import { getDefaultVolume, initLayerDetail } from '../PasteLayerHelper';
import {
  getIngredientByIngIngTypeId,
  getIngredientStock,
  getIngredientTypeName,
  getNewId,
  isNullOrUndefined,
  GetScoreDefn,
} from 'utils/Global';
import { getLatestInspection } from 'components/commandpanel/Navigator/DRCIPWUtil';
import PlateService from 'business/services/PlateService';
import { EGridVaryAlong } from 'enums/ELayersView';
import { Plate, WellNavigationDetails } from 'components/Experiment/constants';
import { getUniqueLayerName } from 'business/plateviewer/GenerateUtil';

const NO_LAYER_TARGET_DROP = -1;

const getLatestImageBatchIdForPlate = (
  sourceExp: ExperimentDtoClient,
  plateId: number,
): number => {
  const latestInspectionSet = getLatestInspection(sourceExp);
  if (!latestInspectionSet?.groupedInspections) {
    return undefined;
  }
  for (const grouped of latestInspectionSet.groupedInspections) {
    if (grouped.plateId === plateId) {
      return grouped.imageBatchId;
    }
  }
  return undefined;
};

const resolveScoreBoundaryIds = (
  scoreDefs: ScoreDefinitionDTO[],
  config: GridOptimizationConfig,
): ScoreBoundaryIds => {
  const empty: ScoreBoundaryIds = {
    exactStart: undefined,
    exactStop: undefined,
    nearStart: undefined,
    nearStop: undefined,
  };
  if (!scoreDefs) {
    return empty;
  }
  const groupScoreDefs = scoreDefs.filter((sd) => sd.scoreGroupName === config.groupName);
  const idOf = (name: string): number | undefined =>
    groupScoreDefs.find((sd) => sd.scoreName === name)?.wellDropScoreId;

  return {
    exactStart: idOf(config.startName),
    exactStop: idOf(config.stopName),
    nearStart: idOf(config.startNearName),
    nearStop: idOf(config.stopNearName),
  };
};

const getBoundaryFromScores = (
  scoresData: ScoresOfWellDropsDTO[][] | undefined,
  scoreIds: ScoreBoundaryIds,
  imageBatchId: number,
  wellNumber: number,
): GridWellBoundary => {
  if (!scoresData || imageBatchId === undefined) {
    return GridWellBoundary.None;
  }

  const dropScores: ScoresOfWellDropsDTO[] = [];
  for (const drops of scoresData) {
    for (const ds of drops) {
      if (
        ds.wellNumber === wellNumber &&
        ds.dropNumber === OPTIMIZATION_DROP_NUMBER &&
        ds.scoreBatchId === imageBatchId
      ) {
        dropScores.push(ds);
      }
    }
  }
  if (dropScores.length === 0) {
    return GridWellBoundary.None;
  }

  const isScored = (scoreId: number | undefined): boolean =>
    scoreId !== undefined && dropScores.some((ds) => ds.wellDropScoreId === scoreId);

  if (isScored(scoreIds.exactStart)) {
    return GridWellBoundary.ExactStart;
  }
  if (isScored(scoreIds.exactStop)) {
    return GridWellBoundary.ExactStop;
  }
  if (isScored(scoreIds.nearStart)) {
    return GridWellBoundary.NearStart;
  }
  if (isScored(scoreIds.nearStop)) {
    return GridWellBoundary.NearStop;
  }
  return GridWellBoundary.None;
};

const getAbsoluteWellLocation = (
  targetPlateIndex: number,
  wellNumber: number,
  targetPlateColumnCount: number,
  sourceContainerCols: number,
  targetContainerRows: number,
  targetContainerCols: number,
): { x: number; y: number } => {
  const plateRowNumber = Math.floor(targetPlateIndex / targetPlateColumnCount);
  const plateColNumber = targetPlateIndex % targetPlateColumnCount;

  const wNum = wellNumber - 1;
  const wellRowNumber = Math.floor(wNum / sourceContainerCols);
  const wellColNumber = wNum % sourceContainerCols;

  return {
    x: targetContainerCols * plateColNumber + wellColNumber,
    y: targetContainerRows * plateRowNumber + wellRowNumber,
  };
};

const getVaryAlongID = (
  numRows: number,
  numColumns: number,
  start: number,
  stop: number,
): number => {
  if (start === stop) {
    return EGridVaryAlong.NONE;
  }
  if (numRows === 1) {
    return EGridVaryAlong.ROW;
  }
  if (numColumns === 1) {
    return EGridVaryAlong.COLUMN;
  }
  return EGridVaryAlong.WELLNUMBER;
};

const getWellLocationLabel = (plate: Plate, wellNumber: number): string => {
  const well = plate.ingredients.wellList.find((w) => w.wellNumber === wellNumber);
  return well ? `${well.rowLetter}${well.columnNumber}` : String(wellNumber);
};

const getGridLocationLabel = (plate: Plate, grid: GridWells): string => {
  const firstLabel = getWellLocationLabel(plate, getFirstWellNumber(grid));
  if (getNumWells(grid) <= 1) {
    return firstLabel;
  }
  return `${firstLabel} - ${getWellLocationLabel(plate, getLastWellNumber(grid))}`;
};

const getIngredientHeaderLabel = (ingredient: GridWellIngredient): string => {
  const typeName = getIngredientTypeName(ingredient.ingredientIngredientTypeId);
  const stock = getIngredientStock(ingredient.ingredientStockId);
  const entity = getIngredientByIngIngTypeId(ingredient.ingredientIngredientTypeId);
  return `${typeName} ${stock?.stockConcentration} ${stock?.concentrationUnits} ${entity?.longName} ${stock?.ingredientId}-${stock?.stockNumber}`;
};

type IngredientErrorGroup = {
  headerLabel: string;
  messages: string[];
};

const createGridLayer = (
  targetExp: ExperimentDtoClient,
  grid: GridWells,
  rect: LayerRect,
): { layer: LayerDTO; ingredientErrorGroups: IngredientErrorGroup[] } => {
  const layer = new LayerDTO();
  layer.id = getNewId();
  layer.expId = targetExp.experimentId;
  layer.x = rect.x;
  layer.y = rect.y;
  layer.width = rect.width;
  layer.height = rect.height;
  layer.type = LayerTypeDTO.Grid;
  layer.layerState = LayerStateDTO.New;
  layer.name = getUniqueLayerName(targetExp.layers ?? [], GRID_LAYER_BASE_NAME);
  layer.volume = getDefaultVolume(targetExp);
  layer.targetDrop = NO_LAYER_TARGET_DROP;
  layer.volumeBasedAddition = true;
  layer.dirty = true;
  layer.locked = false;
  layer.overrideLayer = false;

  const layerDetail = initLayerDetail(
    LayerTypeDTO.Grid,
    layer.id,
    targetExp,
  ) as GridLayerDTO;
  const gridDto = layerDetail.grid as GridDTO;
  gridDto.gridIngredient = gridDto.gridIngredient ?? [];

  const numRows = getNumRows(grid);
  const numColumns = getNumColumns(grid);
  const ingredientErrorGroups: IngredientErrorGroup[] = [];

  for (const ingredient of getGridIngredients(grid)) {
    const ingredientErrors: string[] = [];
    const concRange = getConcentrationRange(grid, ingredient, ingredientErrors);
    const gridIng = new GridIngredientDTO();
    gridIng.id = getNewId();
    gridIng.gridId = gridDto.id;
    gridIng.ingredientIngredientTypeId = ingredient.ingredientIngredientTypeId;
    gridIng.ingredientStockId = ingredient.ingredientStockId;
    gridIng.startConcentration = concRange.startConcentration;
    gridIng.stopConcentration = concRange.stopConcentration;
    gridIng.concentrationVaryAlong = getVaryAlongID(
      numRows,
      numColumns,
      concRange.startConcentration,
      concRange.stopConcentration,
    );

    gridIng.pHvaryAlong = EGridVaryAlong.NONE;

    if (!isPHNull(ingredient)) {
      const phRange = getPHRange(grid, ingredient, ingredientErrors);
      gridIng.startpH = phRange.startPH;
      gridIng.stoppH = phRange.stopPH;
      gridIng.pHvaryAlong = getVaryAlongID(
        numRows,
        numColumns,
        phRange.startPH,
        phRange.stopPH,
      );

      if (!isHighPHIngredientStockIdNull(ingredient)) {
        gridIng.highPhingredientStockId = ingredient.highPhIngredientStockId;
      }
    }
    gridDto.gridIngredient.push(gridIng);

    if (ingredientErrors.length > 0) {
      ingredientErrorGroups.push({
        headerLabel: getIngredientHeaderLabel(ingredient),
        messages: ingredientErrors,
      });
    }
  }

  layer.layerDetail = layerDetail;
  return { layer, ingredientErrorGroups };
};

const createProteinLayer = (
  targetExp: ExperimentDtoClient,
  rect: LayerRect,
  proteinFormulationId: number,
): LayerDTO => {
  const layer = new LayerDTO();
  layer.id = getNewId();
  layer.expId = targetExp.experimentId;
  layer.x = rect.x;
  layer.y = rect.y;
  layer.width = rect.width;
  layer.height = rect.height;
  layer.type = LayerTypeDTO.Protein;
  layer.layerState = LayerStateDTO.New;
  layer.name = getUniqueLayerName(targetExp.layers ?? [], PROTEIN_LAYER_BASE_NAME);
  layer.volume = getDefaultVolume(targetExp);
  layer.targetDrop = OPTIMIZATION_DROP_NUMBER;
  layer.volumeBasedAddition = false;
  layer.dirty = true;
  layer.locked = false;
  layer.overrideLayer = false;

  const layerDetail = new ProteinLayerDTO();
  layerDetail.layerId = layer.id;
  layerDetail.id = getNewId();
  layerDetail.experimentId = targetExp.experimentId;
  layerDetail.proteinLayerProteinFormulation = [];

  const halfVol = targetExp.containerDefDropVolume / 2.0;
  const pf = new ProteinLayerProteinFormulationDTO();
  pf.proteinLayerId = layer.id;
  pf.proteinFormulationId = proteinFormulationId;
  pf.startWellDropVolume = halfVol;
  pf.stopWellDropVolume = halfVol;
  pf.wellDropVolumeVaryAlong = EGridVaryAlong.NONE;
  pf.startProteinDropVolume = halfVol;
  pf.stopProteinDropVolume = halfVol;
  pf.proteinDropVolumeVaryAlong = EGridVaryAlong.NONE;
  layerDetail.proteinLayerProteinFormulation.push(pf);

  layer.layerDetail = layerDetail;
  return layer;
};

const groupWellsBySourcePlate = (
  sourceExp: ExperimentDtoClient,
  selectedWells: WellNavigationDetails[],
): {
  plateWells: Map<number, WellNavigationDetails[]>;
  platesByNumber: Map<number, Plate>;
} => {
  const plateWells = new Map<number, WellNavigationDetails[]>();
  const platesByNumber = new Map<number, Plate>();

  for (const well of selectedWells) {
    const plate = sourceExp.plateDto.find(
      (p) => p.plateId === well.plateID || p.plateNumber === well.plateNum,
    );
    if (!plate || !plate.ingredients) {
      continue;
    }
    platesByNumber.set(plate.plateNumber, plate);
    if (!plateWells.has(plate.plateNumber)) {
      plateWells.set(plate.plateNumber, []);
    }
    plateWells.get(plate.plateNumber).push(well);
  }
  return { plateWells, platesByNumber };
};

const getSharedProteinFormulationId = (
  plateWells: Map<number, WellNavigationDetails[]>,
  platesByNumber: Map<number, Plate>,
): number | undefined => {
  let proteinFormulationId: number | undefined;
  let shared = true;

  for (const [plateNumber, wells] of plateWells) {
    const plate = platesByNumber.get(plateNumber);
    for (const wellSel of wells) {
      const well = plate.ingredients.wellList.find(
        (w) => w.wellNumber === wellSel.wellNum,
      );
      const drop = well?.dropList?.find((d) => d.dropNumber === OPTIMIZATION_DROP_NUMBER);
      if (drop && !isNullOrUndefined(drop.proteinFormulationID)) {
        if (proteinFormulationId === undefined) {
          proteinFormulationId = drop.proteinFormulationID;
        } else if (proteinFormulationId !== drop.proteinFormulationID) {
          shared = false;
        }
      }
    }
  }
  return shared ? proteinFormulationId : undefined;
};

const fetchScoresByPlate = async (
  plateWells: Map<number, WellNavigationDetails[]>,
  platesByNumber: Map<number, Plate>,
): Promise<Map<number, ScoresOfWellDropsDTO[][]>> => {
  const plateService = new PlateService();
  const scoresByPlate = new Map<number, ScoresOfWellDropsDTO[][]>();

  for (const plateNumber of plateWells.keys()) {
    const plate = platesByNumber.get(plateNumber);
    if (plate?.plateId) {
      try {
        const scores = await plateService.getPlateScoreBasedOnWellsByPlateId(
          plate.plateId,
        );
        scoresByPlate.set(plateNumber, scores);
      } catch {
        console.error('An error occured while fetching plate scores.');
      }
    }
  }
  return scoresByPlate;
};

const buildGridWellPlate = (
  plate: Plate,
  wellSelections: WellNavigationDetails[],
  containerRows: number,
  containerCols: number,
  scores: ScoresOfWellDropsDTO[][],
  scoreIds: ScoreBoundaryIds,
  imageBatchId: number,
) => {
  const gridPlate = createGridWellPlate(plate.plateNumber, containerRows, containerCols);

  for (const wellSel of wellSelections) {
    const well = plate.ingredients.wellList.find((w) => w.wellNumber === wellSel.wellNum);
    if (!well) {
      continue;
    }
    const gridWell = createWellInPlate(gridPlate, well.wellNumber);
    for (const wl of well.wellLayerList) {
      for (const wli of wl.wellLayerIngredientList) {
        gridWell.ingredients.push(
          createGridWellIngredient(
            wli.ingredientIngredientTypeId,
            wli.ingredientStockId,
            wli.concentration,
            wli.ingredientTypeIsBuffer ? wli.pH : undefined,
            wli.ingredientTypeIsBuffer && !isNullOrUndefined(wli.highPhIngStockId)
              ? wli.highPhIngStockId
              : undefined,
          ),
        );
      }
    }
    gridWell.boundary = getBoundaryFromScores(
      scores,
      scoreIds,
      imageBatchId,
      well.wellNumber,
    );
  }
  return gridPlate;
};

export const createOptimizedGridLayers = async (
  sourceExp: ExperimentDtoClient,
  targetExp: ExperimentDtoClient,
  selectedWells: WellNavigationDetails[],
  config: GridOptimizationConfig,
): Promise<GridOptimizationResult> => {
  const errors: string[] = [];
  if (!config.gridWellsEnabled || !selectedWells || selectedWells.length === 0) {
    return { expData: targetExp, errors };
  }

  const scoreIds = resolveScoreBoundaryIds(GetScoreDefn(), config);

  const containerRows = sourceExp.containerRows;
  const containerCols = sourceExp.containerCols;
  const targetContainerRows = targetExp.containerRows;
  const targetContainerCols = targetExp.containerCols;
  const targetPlateColumnCount = targetExp.colCount > 0 ? targetExp.colCount : 1;

  const { plateWells, platesByNumber } = groupWellsBySourcePlate(
    sourceExp,
    selectedWells,
  );
  const proteinFormulationId = getSharedProteinFormulationId(plateWells, platesByNumber);
  const scoresByPlate = await fetchScoresByPlate(plateWells, platesByNumber);

  targetExp.layers = targetExp.layers ?? [];
  const rects: LayerRect[] = [];

  const sortedPlateNumbers = Array.from(plateWells.keys()).sort((a, b) => a - b);

  for (let plateIndex = 0; plateIndex < sortedPlateNumbers.length; plateIndex++) {
    const plateNumber = sortedPlateNumbers[plateIndex];
    const plate = platesByNumber.get(plateNumber);
    const gridPlate = buildGridWellPlate(
      plate,
      plateWells.get(plateNumber),
      containerRows,
      containerCols,
      scoresByPlate.get(plateNumber),
      scoreIds,
      getLatestImageBatchIdForPlate(sourceExp, plate.plateId),
    );

    for (const grid of getGrids(gridPlate, config.maxNumRows, config.maxNumColumns)) {
      const absPoint = getAbsoluteWellLocation(
        plateIndex,
        getFirstWellNumber(grid),
        targetPlateColumnCount,
        containerCols,
        targetContainerRows,
        targetContainerCols,
      );

      const rect: LayerRect = {
        x: absPoint.x,
        y: absPoint.y,
        width: getNumColumns(grid) - 1,
        height: getNumRows(grid) - 1,
      };
      rects.push(rect);

      const { layer, ingredientErrorGroups } = createGridLayer(targetExp, grid, rect);
      targetExp.layers.push(layer);

      if (grid.wellErrors.length > 0 || ingredientErrorGroups.length > 0) {
        errors.push(`- Plate ${plateNumber} Well ${getGridLocationLabel(plate, grid)}:`);
        for (const wellError of grid.wellErrors) {
          errors.push(`    ${wellError}`);
        }
        for (const group of ingredientErrorGroups) {
          errors.push(`    ${group.headerLabel}:`);
          for (const message of group.messages) {
            errors.push(`      ${message}`);
          }
        }
      }
    }
  }

  if (proteinFormulationId !== undefined) {
    for (const rect of rects) {
      targetExp.layers.push(createProteinLayer(targetExp, rect, proteinFormulationId));
    }
  }

  return { expData: targetExp, errors };
};
